import { z } from 'zod';
import { CURP_REGEX } from '../utils/index';

// ═══════════════════════════════════════════════════════════════
// ONBOARDING MULTI-PASO
// Schemas y tipos para el flujo de registro completo
// ═══════════════════════════════════════════════════════════════

/**
 * Pasos del onboarding
 */
export const OnboardingSteps = {
  DATA: 'data',
  ADDRESS: 'address',
  CURP: 'curp',
  RFC: 'rfc',
  TRANSACTIONAL_PROFILE: 'transactional_profile',
  BENEFICIARY: 'beneficiary',
  CONTRACT: 'contract',
  COMPLETE: 'complete',
} as const;

export type OnboardingStep = (typeof OnboardingSteps)[keyof typeof OnboardingSteps];

// ───────────────────────────────────────────────────────────────
// Paso 1: Validar Datos personales
// ───────────────────────────────────────────────────────────────

export const dataStepSchema = z.object({
  name: z.string().optional(),
  secondName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  genre: z.string().optional(),
  birthDate: z.string().optional(),
  maritalStatus: z.string().optional(),
  birthCountry: z.string().optional(),
  stateOfBirth: z.string().optional(),
  nationality: z.string().optional(),
  birthPlace: z.string().optional(),
});

// ───────────────────────────────────────────────────────────────
// Paso 2: Validar Dirección
// ───────────────────────────────────────────────────────────────

export const addressStepSchema = z.object({
  postalCode: z.string().optional(),
  country: z.string().optional(),
  state: z.string().optional(),
  city: z.string().optional(),
  neighborhood: z.string().optional(),
  street: z.string().optional(),
  exteriorNumber: z.string().optional(),
  interiorNumber: z.string().optional(),
});

// ───────────────────────────────────────────────────────────────
// Paso 3: CURP
// ───────────────────────────────────────────────────────────────

export const curpStepSchema = z.object({
  /** CURP extraído del documento de identidad */
  curp: z
    .string()
    .length(18, 'El CURP debe tener 18 caracteres')
    .regex(CURP_REGEX, 'El formato del CURP no es válido'),
  /** Constancia de CURP en PDF (solo si el CURP extraído no coincide) */
  document: z.instanceof(File).optional(),
});

// ───────────────────────────────────────────────────────────────
// Paso 4: RFC
// ───────────────────────────────────────────────────────────────

export const rfcStepSchema = z.object({
  /** RFC extraído del documento de identidad */
  rfc: z
    .string()
    .min(12, 'El RFC debe tener al menos 12 caracteres')
    .max(13, 'El RFC no puede tener más de 13 caracteres')
    .regex(/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/, 'El formato del RFC no es válido')
    .optional(),
  /** Constancia de Situación Fiscal en PDF (opcional si el RFC existe) */
  document: z.instanceof(File).optional(),
});

// ───────────────────────────────────────────────────────────────
// Paso 5: Perfil transaccional — Perfil económico y transaccional
// ───────────────────────────────────────────────────────────────

export const occupationCodeSchema = z.enum([
  'empleado',
  'empleado_publico',
  'empresario',
  'profesionista',
  'comerciante',
  'estudiante',
  'ama_casa',
  'jubilado',
  'desempleado',
  'otro',
]);

export const economicActivitySchema = z.enum([
  'agricultura',
  'comercio',
  'comercio_mayor',
  'construccion',
  'educacion',
  'finanzas',
  'manufactura',
  'salud',
  'tecnologia',
  'transporte',
  'turismo',
  'otro',
]);

export const fundsSourceSchema = z.enum([
  'salario',
  'negocio',
  'honorarios',
  'renta',
  'inversiones',
  'pension',
  'herencia',
  'remesas',
  'otro',
]);

export const fundsDestinationSchema = z.enum([
  'ahorro',
  'gastos_personales',
  'pago_deudas',
  'negocio',
  'educacion',
  'vivienda',
  'remesas',
  'otro',
]);

export const monthlyAmountRangeSchema = z.enum([
  'menos_5k',
  '5k_20k',
  '20k_50k',
  '50k_100k',
  '100k_500k',
  'mas_500k',
]);

export const numeroOperacionesSchema = z.enum(['1_5', '6_10', '11_20', '21_50', 'mas_50']);

export const operationFrequencySchema = z.enum([
  'diaria',
  'semanal',
  'quincenal',
  'mensual',
  'esporadica',
]);

export const accountStepSchema = z.object({
  occupation_code: occupationCodeSchema,
  economic_activity: economicActivitySchema,
  monthly_income: z.number().nonnegative().optional(),
  funds_source: fundsSourceSchema,
  funds_destination: fundsDestinationSchema,
  monthly_amount_range: monthlyAmountRangeSchema,
  numero_operaciones: numeroOperacionesSchema.optional(),
  operation_frequency: operationFrequencySchema,
});

// ───────────────────────────────────────────────────────────────
// Paso 6: Beneficiario
// ───────────────────────────────────────────────────────────────

export const beneficiaryRelationshipSchema = z.enum([
  'conyuge',
  'hijo',
  'padre',
  'hermano',
  'abuelo',
  'nieto',
  'otro',
]);

export const beneficiaryAddressSchema = z.object({
  postalCode: z.string().optional(),
  country: z.enum(['mx', 'us', 'ca', 'es', 'ar', 'co', 'otro']).optional(),
  city: z.string().optional(),
  state: z.enum(['ags', 'cdmx', 'jal', 'nl', 'pue', 'qro', 'ver', 'yuc']).optional(),
  municipality: z.string().optional(),
  neighborhood: z.string().optional(),
  street: z.string().optional(),
  exteriorNumber: z.string().optional(),
  interiorNumber: z.string().optional(),
});

export const beneficiaryStepSchema = z.object({
  firstName: z.string().min(1, 'El nombre es requerido'),
  secondName: z.string().optional(),
  lastName: z.string().min(1, 'El apellido paterno es requerido'),
  secondLastName: z.string().optional(),
  birthDate: z.string().min(1, 'La fecha de nacimiento es requerida'),
  phone: z
    .string()
    .length(10, 'El teléfono debe tener 10 dígitos')
    .regex(/^\d+$/, 'El teléfono solo debe contener números'),
  relationship: beneficiaryRelationshipSchema,
  percentage: z.number().default(100),
  useMyAddress: z.boolean().default(false),
  address: beneficiaryAddressSchema.optional(),
});

// ───────────────────────────────────────────────────────────────
// Paso 8: Firma de contratos
// ───────────────────────────────────────────────────────────────

export const contractSignatureSchema = z.object({
  accepted: z.literal(true, {
    errorMap: () => ({ message: 'Debes aceptar todos los contratos para continuar' }),
  }),
});

// ═══════════════════════════════════════════════════════════════
// Schema completo del formulario de onboarding multi-paso
// ═══════════════════════════════════════════════════════════════

const onboardingStepEnum = z.enum([
  'data',
  'address',
  'curp',
  'rfc',
  'transactional_profile',
  'beneficiary',
  'contract',
  'complete',
]);

export const onboardingFormSchema = z.object({
  // Step tracking
  currentStep: onboardingStepEnum,
  completedSteps: z.array(onboardingStepEnum),

  // Paso 1: Datos personales
  data: dataStepSchema.optional(),

  // Paso 2: Dirección
  address: addressStepSchema.optional(),

  // Paso 3: CURP
  curp: curpStepSchema.optional(),

  // Paso 4: RFC
  rfc: rfcStepSchema.optional(),

  // Paso 5: Perfil transaccional
  transactional_profile: accountStepSchema.optional(),

  // Paso 6: Beneficiario
  beneficiary: beneficiaryStepSchema.optional(),
});

// ═══════════════════════════════════════════════════════════════
// Tipos inferidos
// ═══════════════════════════════════════════════════════════════

export type DataStepData = z.infer<typeof dataStepSchema>;
/** @deprecated Usa DataStepData */
export type PersonalDataStepData = DataStepData;

export type AddressStepData = z.infer<typeof addressStepSchema>;
export type CurpStepData = z.infer<typeof curpStepSchema>;
export type RfcStepData = z.infer<typeof rfcStepSchema>;
export type AccountStepData = z.infer<typeof accountStepSchema>;
export type BeneficiaryAddressData = z.infer<typeof beneficiaryAddressSchema>;
export type BeneficiaryStepData = z.infer<typeof beneficiaryStepSchema>;
export type ContractSignatureData = z.infer<typeof contractSignatureSchema>;
export type OnboardingFormData = z.infer<typeof onboardingFormSchema>;

// Tipos de enums
export type OccupationCode = z.infer<typeof occupationCodeSchema>;
export type EconomicActivity = z.infer<typeof economicActivitySchema>;
export type FundsSource = z.infer<typeof fundsSourceSchema>;
export type FundsDestination = z.infer<typeof fundsDestinationSchema>;
export type MonthlyAmountRange = z.infer<typeof monthlyAmountRangeSchema>;
export type OperationFrequency = z.infer<typeof operationFrequencySchema>;
export type BeneficiaryRelationship = z.infer<typeof beneficiaryRelationshipSchema>;
